// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';

// ** Components
import Loader from '@/components/common/Loader';
import Section from '@/components/common/Section';
import { UserDashboard, type DashboardProps } from '@/components/common/Dashboard';

// ** Utils
import { useGetUserEvents } from '@/lib/utils/eventDataUtils';
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';
import { useGetEhrenamtDocumentsConnection } from '@/lib/utils/ehrenamtDataUtils';
import { formatAddressLine, normalizeDateLabel } from '@/utils/shared/stringUtils';
import { useGetDashboardConsulting, useGetDashboardUserData } from '@/lib/utils/dashboardDataUtils';

// ** Types
import type { NextPageWithLayout } from '@/types/page';
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentDashboardOverview as DashboardOverviewProps } from '@/types/graphql';

type ComponentDashboardOverviewProps = StrapiComponentProps<DashboardOverviewProps>;

const ComponentDashboardOverview: NextPageWithLayout<ComponentDashboardOverviewProps> = ({
	currentUser,
	containerSx,
	dashboardProfile,
	dashboardSections
}: ComponentDashboardOverviewProps) => {
	const { total, loading: isLoading } = useGetEhrenamtDocumentsConnection(currentUser?.groups || []);

	const {
		data: { userDashboard }
	} = useGetDashboardUserData(Number(currentUser?.cobraId));

	const {
		data: { dashboardConsulting }
	} = useGetDashboardConsulting(Number(currentUser?.cobraId));

	const { userEvents } = useGetUserEvents(Number(currentUser?.cobraId));

	const { person } = userDashboard || {};
	const { name, address, contactData } = person || {};

	const showVolunteerSection = React.useMemo(() => {
		const hasEhrenamtGroup = currentUser?.groups?.some((group: string) =>
			['02 Ehrenamt', '02 Fachausschuss NRW', 'Ausschuss_', 'Funktion_'].some((prefix) => group.startsWith(prefix))
		);

		return !!hasEhrenamtGroup && total > 0;
	}, [currentUser?.groups, total]);

	const dashboardData: DashboardProps = React.useMemo(() => {
		const firstName = name?.firstName || 'User';
		const lastName = name?.lastName || '';
		const displayName = name?.firstName ? `${firstName} ${lastName}`.trim() : 'Gast';
		const emailValue = contactData?.email;

		// Helper to get CMS config for a specific section type
		const getSection = (type: 'Events' | 'Consulting' | 'Volunteer' | 'Favorites') =>
			dashboardSections?.find((section) => section?.dashboardSectionType === type);

		const eventsConfig = getSection('Events');
		const consultingConfig = getSection('Consulting');
		const volunteerConfig = getSection('Volunteer');
		const favoritesConfig = getSection('Favorites');

		// Helper to safely format CTAs to match DashboardLink
		const formatCta = (configCta: any, defaultLabel: string, defaultHref: string) => {
			if (!configCta) return { label: defaultLabel, href: defaultHref };

			const resolvedHref = buildPathFromRelationLink(configCta.href);

			return {
				label: configCta.label || defaultLabel,
				href: resolvedHref
			};
		};

		return {
			welcomeText: `Herzlich Willkommen, ${firstName}!`,
			profile: {
				title: dashboardProfile?.dashboardProfileTitle || 'Mein Profil',
				items: [
					{
						id: 'profile-name',
						label: 'Name:',
						value: displayName,
						icon: 'person' as const
					},
					...(address
						? [
								{
									id: 'profile-address',
									label: 'Adresse:',
									value: formatAddressLine({
										street: address.street ?? undefined,
										houseNumber: address.houseNumber == null ? undefined : String(address.houseNumber),
										postalCode: address.postalCode ?? undefined,
										city: address.city ?? undefined
									}),
									icon: 'address' as const
								}
							]
						: []),
					{
						id: 'profile-email',
						label: 'E-Mail:',
						value: emailValue ? String(emailValue) : 'Keine E-Mail hinterlegt',
						icon: 'email' as const
					},
					...(contactData?.mobilePhone
						? [
								{
									id: 'profile-mobile',
									label: 'Mobil:',
									value: contactData.mobilePhone,
									icon: 'mobile' as const
								}
							]
						: []),
					...(contactData?.phone
						? [
								{
									id: 'profile-phone',
									label: 'Festnetznummer:',
									value: contactData.phone,
									icon: 'phone' as const
								}
							]
						: [])
				]
			},

			// 1. Events
			events: eventsConfig
				? {
						title: eventsConfig.dashboardSectionTitle || 'Meine Veranstaltungen',
						cta: formatCta(eventsConfig.dashboardSectionCta, 'Alle Veranstaltungen', '#'),
						items:
							userEvents?.map((event, index) => ({
								id: event?.id ?? `fallback-id-${index}`,
								headline: event?.title || 'Termin',
								date: normalizeDateLabel(event?.start)
							})) || []
					}
				: undefined,

			// 2. Consulting
			consulting:
				consultingConfig && dashboardConsulting?.length > 0
					? {
							title: consultingConfig.dashboardSectionTitle || 'Meine Beratungen',
							cta: formatCta(consultingConfig.dashboardSectionCta, 'Alle Beratungen', '#'),
							collapsible: consultingConfig.dashboardSectionCollapsible,
							defaultExpanded: consultingConfig.dashboardSectionDefaultExpanded,
							items: dashboardConsulting.map((e) => e?.consultingAssignment || 'Unbekannte Beratung')
						}
					: undefined,

			// 3. Volunteer
			volunteer:
				volunteerConfig && showVolunteerSection
					? {
							title: volunteerConfig.dashboardSectionTitle || 'Mein Ehrenamt',
							cta: formatCta(volunteerConfig.dashboardSectionCta, 'Alle Ehrenamtler', '#')
						}
					: undefined,

			// 4. Favorites
			favorites: favoritesConfig
				? {
						title: favoritesConfig.dashboardSectionTitle || 'Meine Favoriten',
						cta: formatCta(favoritesConfig.dashboardSectionCta, 'Alle Favoriten', '#')
					}
				: undefined
		};
	}, [
		address,
		contactData,
		name,
		dashboardProfile,
		dashboardSections,
		dashboardConsulting,
		showVolunteerSection,
		userEvents
	]);

	if (isLoading) return <Loader />;

	return (
		<Section containerSx={containerSx} maxWidthPx={1648} backgroundColor="heroLightBackground.main">
			<Box sx={{ pb: { xs: 8, md: 10 } }}>
				<UserDashboard {...dashboardData} showVolunteerSection={showVolunteerSection} />
			</Box>
		</Section>
	);
};

export default ComponentDashboardOverview;
